/**
 * Reusable provider-neutral hosted payment dialog.
 *
 * The browser notification only starts verification. Payment is accepted only
 * after the generic payment-session status endpoint confirms it server-side.
 *
 * Retrying is offered for a confirmed decline and withheld for everything
 * else. An unresolved charge that invites another attempt is how a single
 * payment becomes two.
 */
import React from 'react';
import {nanoid} from '@reduxjs/toolkit';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import {
    createPaymentSession,
    getPaymentSessionStatus,
    PaymentSessionStatus,
} from 'api/customer/payment-session.api';

export interface PaymentPaidResult {
    transactionId: string;
    last4: string | null;
    provider: string;
    /** Reusable provider token (not a PAN), when supplied by the provider. */
    token: string | null;
    tokenExpiry: string | null;
    receiptState?: PaymentSessionStatus['receipt_state'];
    receiptId?: number | null;
    receiptError?: string | null;
    /** Card installments chosen before checkout (1 = single charge). */
    installments?: number;
}

interface Props {
    open: boolean;
    amount: number;
    purpose?: string;
    refId?: string;
    contact?: string;
    company?: string;
    email?: string;
    phone?: string;
    address?: string;
    city?: string;
    zip?: string;
    /** Hosted checkout method. ``bit`` uses Tranzila Bit Init (QR), not the card iframe. */
    method?: 'bit' | 'credit';
    /** Number of card installments (1 = single charge). Shown when allowInstallments. */
    paymentPlan?: number;
    /** Credit installments (תשלומים). Off for Bit. */
    allowInstallments?: boolean;
    onPaid: (result: PaymentPaidResult) => void;
    onClose: () => void;
}

const INSTALLMENT_MAX = 12;
/** ~90s at 1.5s per poll: SHVA can take far longer than a few seconds. */
const VERIFY_ATTEMPTS = 60;
const VERIFY_INTERVAL_MS = 1500;
const DECLINED_MESSAGE = 'הסליקה לא אושרה. ניתן לנסות שוב.';
const REVIEW_MESSAGE =
    'התקבל חיוב שלא ניתן לאמת מול ספק התשלום. אין לחייב שוב — '
    + 'יש לבדוק בטרנזילה אם הכרטיס חויב.';
const UNRESOLVED_MESSAGE =
    'לא התקבלה תשובה סופית מספק התשלום. ייתכן שהכרטיס חויב — '
    + 'יש לבדוק בטרנזילה לפני ניסיון נוסף.';

function providerLabel(provider: string | null): string {
    if (!provider) return '';
    return provider.charAt(0).toUpperCase() + provider.slice(1);
}

const PaymentIframeDialog = ({
    open, amount, purpose, refId, contact, company, email, phone, address, city, zip,
    method, paymentPlan, allowInstallments = true, onPaid, onClose,
}: Props) => {
    const bitOnly = method === 'bit';
    const canEmbed = window.location.protocol === 'https:';
    const [iframeUrl, setIframeUrl] = React.useState<string | null>(null);
    const [provider, setProvider] = React.useState<string | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    const [retryable, setRetryable] = React.useState(false);
    const [verifying, setVerifying] = React.useState(false);
    const [starting, setStarting] = React.useState(false);
    const [installments, setInstallments] = React.useState(
        paymentPlan && paymentPlan > 0 ? paymentPlan : 1,
    );
    const sessionRef = React.useRef<string | null>(null);
    const doneRef = React.useRef(false);
    const cancelledRef = React.useRef(false);
    /**
     * One key per payment, kept across retries so the provider can refuse a
     * duplicate charge. A retry must not mint a new one.
     */
    const intentKeyRef = React.useRef<string>(nanoid());
    const installmentsRef = React.useRef(installments);
    installmentsRef.current = installments;

    const fail = React.useCallback((message: string, canRetry: boolean) => {
        setVerifying(false);
        setRetryable(canRetry);
        setError(message);
    }, []);

    const startCheckout = React.useCallback(async (plan: number) => {
        setStarting(true);
        setError(null);
        setRetryable(false);
        try {
            const session = await createPaymentSession(amount, {
                purpose,
                ref: refId,
                client_id: refId && Number.isFinite(Number(refId)) ? Number(refId) : undefined,
                contact,
                company,
                email,
                phone,
                address,
                city,
                zip,
                method: bitOnly ? 'bit' : method,
                payment_plan: allowInstallments ? plan : undefined,
                intent_key: intentKeyRef.current,
            });
            if (cancelledRef.current) return;
            sessionRef.current = session.session_id;
            setProvider(session.provider);
            setIframeUrl(session.checkout_url || session.iframe_url || null);
        } catch (e: any) {
            // Nothing was charged: the session never opened.
            if (!cancelledRef.current) fail(e?.message || 'פתיחת עמוד תשלום נכשלה', true);
        } finally {
            if (!cancelledRef.current) setStarting(false);
        }
    }, [amount, purpose, refId, contact, company, email, phone, address, city, zip, method, bitOnly, allowInstallments, fail]);

    React.useEffect(() => {
        if (!open) {
            setIframeUrl(null);
            setProvider(null);
            setError(null);
            setRetryable(false);
            setVerifying(false);
            setStarting(false);
            sessionRef.current = null;
            doneRef.current = false;
            cancelledRef.current = true;
            return;
        }
        cancelledRef.current = false;
        // A fresh dialog is a new payment; retries within it keep this key.
        intentKeyRef.current = nanoid();
        setInstallments(paymentPlan && paymentPlan > 0 ? paymentPlan : 1);
        if (bitOnly && amount > 0 && amount < 5) {
            fail('סכום מינימום לחיוב ביט הוא 5 ש"ח', false);
            return () => {
                cancelledRef.current = true;
            };
        }
        if (!allowInstallments) {
            void startCheckout(1);
        }
        return () => {
            cancelledRef.current = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const verify = React.useCallback(async (opts?: {
        maxAttempts?: number;
        /** The provider already redirected to its failure URL. */
        unresolvedIsDecline?: boolean;
    }) => {
        const maxAttempts = opts?.maxAttempts ?? VERIFY_ATTEMPTS;
        const sessionId = sessionRef.current;
        if (!sessionId || doneRef.current) return;
        setVerifying(true);
        for (let i = 0; i < maxAttempts; i++) {
            // Two provider notifications can start two loops; only one of
            // them may report the payment.
            if (doneRef.current || cancelledRef.current) return;
            let status: PaymentSessionStatus | null = null;
            try {
                status = await getPaymentSessionStatus(sessionId);
            } catch {
                status = null;
            }
            if (doneRef.current || cancelledRef.current) return;
            if (status?.provider) setProvider(status.provider);
            if (status?.status === 'paid') {
                doneRef.current = true;
                onPaid({
                    transactionId: status.transaction_id ?? '',
                    last4: status.last4,
                    provider: status.provider || provider || '',
                    token: status.token ?? null,
                    tokenExpiry: status.token_expiry ?? null,
                    receiptState: status.receipt_state,
                    receiptId: status.receipt_id,
                    receiptError: status.receipt_error,
                    installments: allowInstallments ? installmentsRef.current : 1,
                });
                return;
            }
            if (status?.status === 'failed') {
                fail(DECLINED_MESSAGE, true);
                return;
            }
            if (status?.status === 'needs_review') {
                fail(REVIEW_MESSAGE, false);
                return;
            }
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => setTimeout(resolve, VERIFY_INTERVAL_MS));
        }
        // Still 'pending' after the whole window. The card may well have been
        // charged, so this is not a decline and must not offer a retry.
        fail(
            opts?.unresolvedIsDecline ? DECLINED_MESSAGE : UNRESOLVED_MESSAGE,
            !!opts?.unresolvedIsDecline,
        );
    }, [onPaid, provider, allowInstallments, fail]);

    const openInSecureTab = React.useCallback(() => {
        if (!iframeUrl) return;
        const paymentWindow = window.open(iframeUrl, '_blank');
        if (!paymentWindow) {
            // Nothing was charged: the payment page never opened.
            fail('הדפדפן חסם את חלון התשלום. יש לאפשר חלונות קופצים ולנסות שוב.', true);
            return;
        }
        paymentWindow.focus();
        void verify({maxAttempts: 400});
    }, [iframeUrl, verify, fail]);

    const onRetry = React.useCallback(() => {
        // Same intent key on purpose, so the provider can still block a
        // duplicate if the previous attempt did go through after all.
        setError(null);
        setRetryable(false);
        setIframeUrl(null);
        sessionRef.current = null;
        void startCheckout(allowInstallments ? installmentsRef.current : 1);
    }, [startCheckout, allowInstallments]);

    React.useEffect(() => {
        if (!open) return undefined;
        const onMessage = (event: MessageEvent) => {
            const data: any = event.data;
            if (!data || (data.source !== 'nk-payment' && data.source !== 'nk-tranzila')) return;
            if (data.session_id && sessionRef.current && data.session_id !== sessionRef.current) return;
            // Either way the server decides. A browser-reported failure still
            // gets a short confirmation window, because the notify callback
            // is the only channel that can tell a decline from a timeout.
            void verify(data.ok ? undefined : {maxAttempts: 6, unresolvedIsDecline: true});
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [open, verify]);

    const displayProvider = providerLabel(provider);
    const waitingForPlan = allowInstallments && !iframeUrl && !error && !starting && !verifying;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <LockIcon fontSize="small" color="success" />
                <Typography variant="h4" component="span">
                    סליקה מאובטחת{displayProvider ? ` באמצעות ${displayProvider}` : ''}
                    {amount ? ` — ${amount.toFixed(2)} ₪` : ''}
                    {bitOnly ? ' (ביט)' : ' (אשראי / ביט)'}
                </Typography>
                <Box sx={{flex: 1}} />
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{p: waitingForPlan ? 3 : 0, height: waitingForPlan ? 'auto' : 560}}>
                {waitingForPlan && (
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                        <FormControl fullWidth>
                            <InputLabel id="hosted-installments-label">תשלומים</InputLabel>
                            <Select
                                labelId="hosted-installments-label"
                                label="תשלומים"
                                value={installments}
                                onChange={(e) => setInstallments(Number(e.target.value))}
                            >
                                {Array.from({length: INSTALLMENT_MAX}, (_, i) => i + 1).map((n) => (
                                    <MenuItem key={n} value={n}>
                                        {n === 1 ? 'תשלום אחד' : `${n} תשלומים`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {installments > 1 && amount > 0 && (
                            <Typography variant="caption" color="text.secondary">
                                סכום לכל תשלום (חלוקה שווה): ₪{(amount / installments).toFixed(2)}
                            </Typography>
                        )}
                        <Button
                            variant="contained"
                            color="success"
                            onClick={() => void startCheckout(installments)}
                        >
                            המשך לתשלום
                        </Button>
                    </Box>
                )}
                {error && (
                    <Box sx={{p: waitingForPlan ? 0 : 3}}>
                        <Alert severity={retryable ? 'error' : 'warning'}>{error}</Alert>
                        {retryable && (
                            <Button
                                fullWidth
                                variant="contained"
                                color="success"
                                sx={{mt: 2}}
                                onClick={onRetry}
                            >
                                נסה שוב
                            </Button>
                        )}
                        <Button fullWidth variant="outlined" sx={{mt: 2}} onClick={onClose}>
                            סגור
                        </Button>
                    </Box>
                )}
                {!error && !waitingForPlan && (verifying || !iframeUrl) && (
                    <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, pt: 10}}>
                        <CircularProgress />
                        <Typography color="textSecondary">
                            {verifying
                                ? `מאמת את התשלום${displayProvider ? ` מול ${displayProvider}` : ''}...`
                                : 'פותח עמוד תשלום מאובטח...'}
                        </Typography>
                    </Box>
                )}
                {!error && !verifying && iframeUrl && canEmbed && (
                    <iframe
                        title="hosted-payment"
                        src={iframeUrl}
                        allow="payment"
                        style={{width: '100%', height: '100%', border: 0}}
                    />
                )}
                {!error && !verifying && iframeUrl && !canEmbed && (
                    <Box sx={{p: 3, display: 'flex', flexDirection: 'column', gap: 2}}>
                        <Alert severity="info">
                            Tranzila מאפשרת הטמעה רק מאתר HTTPS. בסביבת הפיתוח עמוד התשלום
                            ייפתח בלשונית מאובטחת חדשה.
                        </Alert>
                        <Button variant="contained" color="success" onClick={openInSecureTab}>
                            פתח עמוד תשלום מאובטח
                        </Button>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default PaymentIframeDialog;
